import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { planService } from '../../application/PlanService';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import { PlanStatus } from '../../domain/plan/PlanStatus';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveActivePlan } from './useRetrieveActivePlan';

type UseUpdateFlatPlansStatusProps = {
  flatPlans: Array<PlanFlat>;
  status: PlanStatus;
};

type UseUpdateFlatPlansStatusResponse = ServiceResponse<
  ({ flatPlans, status }: UseUpdateFlatPlansStatusProps) => void,
  null
>;

export const useUpdateFlatPlansStatus = (): UseUpdateFlatPlansStatusResponse => {
  const { data: activePlan } = useRetrieveActivePlan();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const queryClient = useQueryClient();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ flatPlans, status }: UseUpdateFlatPlansStatusProps) =>
      planService.updateFlatPlansStatus({
        flatPlans,
        status,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([`planner_search_plans`]);
        await queryClient.invalidateQueries([`planner_flat_plans`]);

        if (activePlan) {
          await queryClient.invalidateQueries([`planner_active_plan`, activePlan.id]);
        }
      },
      onError: (err: any) => {
        console.info(`Update Flat Plans Status Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    isLoading,
    error: isError,
    success: isSuccess,
    run: mutate,
  };
};
